import type { ForecastDataPoint } from 'src/types/domain/ForecastDataPoint';
import type { StateCreator } from 'zustand';

export const buildForecastStoreKey = (sku: string, scopeId = 'default') =>
  `${scopeId}:${sku}`;

export type ForecastSlice = {
  forecastData: Record<string, ForecastDataPoint[]>;
  setForecastData: (
    sku: string,
    data: ForecastDataPoint[],
    scopeId?: string,
  ) => void;
  clearForecastData: (sku?: string, scopeId?: string) => void;
  updateForecastProposal: (
    sku: string,
    monthId: string,
    value: number,
    scopeId?: string,
  ) => void;
};

const createSetForecastData =
  (set: Parameters<StateCreator<ForecastSlice, [], [], ForecastSlice>>[0]) =>
  (sku: string, data: ForecastDataPoint[], scopeId = 'default') =>
    set((state: ForecastSlice) => ({
      forecastData: {
        ...state.forecastData,
        [buildForecastStoreKey(sku, scopeId)]: data,
      },
    }));

const createClearForecastData =
  (set: Parameters<StateCreator<ForecastSlice, [], [], ForecastSlice>>[0]) =>
  (sku?: string, scopeId = 'default') =>
    set((state: ForecastSlice) => ({
      forecastData: sku
        ? Object.fromEntries(
            Object.entries(state.forecastData).filter(
              ([key]) => key !== buildForecastStoreKey(sku, scopeId),
            ),
          )
        : {},
    }));

const createUpdateForecastProposal =
  (set: Parameters<StateCreator<ForecastSlice, [], [], ForecastSlice>>[0]) =>
  (sku: string, monthId: string, value: number, scopeId = 'default') =>
    set((state: ForecastSlice) => ({
      forecastData: {
        ...state.forecastData,
        [buildForecastStoreKey(sku, scopeId)]:
          state.forecastData[buildForecastStoreKey(sku, scopeId)]?.map(
            (item: ForecastDataPoint) =>
              item.month_id === monthId
                ? { ...item, forecastProposal: value }
                : item,
          ) || [],
      },
    }));

const createForecastSlice = (
  set: Parameters<StateCreator<ForecastSlice, [], [], ForecastSlice>>[0],
) => ({
  forecastData: {},
  setForecastData: createSetForecastData(set),
  clearForecastData: createClearForecastData(set),
  updateForecastProposal: createUpdateForecastProposal(set),
});

export const forecastSlice: StateCreator<ForecastSlice, [], [], ForecastSlice> =
  createForecastSlice;
